/**
 * Pure helpers shared by every saved-view presentation (pills, strip, menu,
 * controller) so there is exactly one implementation of "what URL does this
 * view point to" and "was this click a browser-handled click (new tab,
 * modifier key, non-primary button)".
 */

import type React from 'react';

import type { ActiveListView } from '../../core/types';
import {
  ACTIVE_VIEW_PARAM,
  encodeActiveListViewParam,
} from '../../hooks/useActiveListView';
import { MODE_PARAM } from '../urlListParams';
import type { StripItem } from './savedViewTypes';

/**
 * Build the canonical `?view=` href for a given view, preserving `?mode=`
 * from the current URL. Filters/sort/search are intentionally NOT copied —
 * selecting a view replaces them.
 */
export function buildViewHref(
  pathname: string,
  currentSearch: URLSearchParams,
  view: ActiveListView | null
): string {
  const params = new URLSearchParams();
  params.set(ACTIVE_VIEW_PARAM, encodeActiveListViewParam(view));
  const mode = currentSearch.get(MODE_PARAM);
  if (mode !== null) {
    params.set(MODE_PARAM, mode);
  }
  return `${pathname}?${params.toString()}`;
}

/** Map a strip item to the active-view identity it selects, or null for "All". */
export function stripItemToActiveView(item: StripItem): ActiveListView | null {
  if (item.kind === 'all') {
    return null;
  }
  if (item.kind === 'builtin') {
    return { kind: 'builtin', id: item.view.id };
  }
  return { kind: 'saved', id: item.view.entity_id };
}

/**
 * Whether a click should be left to the browser's default handling (open in
 * new tab/window, or a non-primary button) rather than intercepted for
 * client-side navigation.
 *
 * This exists because saved-view links intentionally render as native `<a>`
 * elements rather than react-aria's `AriaLink` — react-aria's press
 * callbacks have no modifier guard of their own and would fire on
 * Cmd/Ctrl-click too, breaking open-in-new-tab.
 */
export function isModifiedClick(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}
